import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react'
import {
  Row,
  Col,
  Button,
  Layout,
  Progress,
  Form,
  Result,
  Spin,
  Descriptions,
  Slider,
  Switch,
  Typography,
} from 'antd'

import { useLyricProcessor } from 'hooks/lyric'
import { useEventsPlayer, useTimelineEvents } from 'hooks/TimelineEvents'
import { FormS3Resource } from 'forms/S3Resource/FormS3Resource'
import {
  PlaySquareOutlined,
  SmileOutlined,
  AppstoreOutlined,
  FrownOutlined,
  FullscreenOutlined,
} from '@ant-design/icons'
import { CanvasSlides } from 'components/KaraokeSlides'
import { useKaraokeDownload } from 'hooks/karaokeDownload'
import { BIG_CANVAS_PROPS } from 'constans/konva'
import { Link } from 'react-router-dom'
import { Player } from 'components/Player/Player'
import { FormSearchSong } from 'forms/SearchSong/FormSearchSong'
import { FontSizer } from './FontSizer'
import './styles.scss'
import { has, get } from 'lodash'
import { FacetsProvider } from 'utils/algolia'

const { Header, Content } = Layout
const { Text } = Typography

const ResultEmpty = ({ icon: Icon, text, ...props }) => (
  <Result icon={<Icon />} title={text} {...props} />
)
ResultEmpty.defaultProps = {
  icon: SmileOutlined,
  text: 'Add the url to your kr file above',
}
const ResultError = ({ error }) => (
  <ResultEmpty text={error} icon={FrownOutlined} status="error" />
)

const Status = ({ loading, progress, processing, error, data }) => {
  if (!loading && error) return <ResultError error={error} />

  if (!loading && !data) return <ResultEmpty />

  if (!loading) return <></>

  if (!processing) return <Progress type="circle" percent={progress} />
  else return <Spin size="large" tip="Loading your song" />
}
const SettingsBox = ({ frameRef }) => {
  const openFullscreen = useCallback(() => {
    if (!frameRef.current) return

    frameRef.current.requestFullscreen()
    frameRef.current.style
    // const canvasEl = frameRef.current.querySelector('.konvajs-content')
    // if (!canvasEl) return

    // const scale = window.outerWidth / BIG_CANVAS_PROPS.width
    // canvasEl.style.transform = `scale(${scale})`
  }, [])
  return (
    <Col
      flex="110px"
      className="settings-box"
      style={{ '--window-w': window.outerWidth }}
    >
      <FontSizer />
      <Button
        size="large"
        type="link"
        icon={<FullscreenOutlined style={{ fontSize: 24 }} />}
        className="full-mode"
        onClick={openFullscreen}
      />
    </Col>
  )
}

export const LayoutPlayer = (props) => {
  const frameRef = useRef()
  const [selectedSong, setSong] = useState({})
  const [playWithLyrics, setPlayWithLyrics] = useState(false)
  const { resetLyricProcessor } = useLyricProcessor()
  const { resetTimelineEvents } = useTimelineEvents()
  const {
    startDownload,
    progress,
    finished,
    loading,
    processing,
    error,
    data: resource,
  } = useKaraokeDownload()

  const audioRef = useRef()
  const audioWithLyricsRef = useRef()
  const { play, pause } = useEventsPlayer({ audioRef })

  const counterProps = useMemo(() => ({ autoStart: false }), [])
  const playerActions = useMemo(() => ({ play, pause }), [])
  const hasSongWithLyrics = useMemo(() => !has(resource, 'audioWithLyrics'), [
    resource,
  ])

  const handleSearchItem = useCallback((data) => {
    setSong(data)
    startDownload(data)
  }, [])
  const handlePlayWithLyrics = useCallback((checked) => {
    if (checked) {
      if (audioRef.current) audioRef.current.muted = true
      if (audioWithLyricsRef.current) audioWithLyricsRef.current.muted = false
    } else {
      if (audioRef.current) audioRef.current.muted = false
      if (audioWithLyricsRef.current) audioWithLyricsRef.current.muted = true
    }
    setPlayWithLyrics(checked)
  }, [])

  useEffect(() => {
    if (!resource) return

    // Set the lyrics text into processor
    if (resource.lyrics) resetLyricProcessor(resource.lyrics)

    // Set the event to play
    if (resource.instructions) resetTimelineEvents(resource.instructions)

    // setTimeout(play, 500)
  }, [resource])

  // Pause the audio if new karaoke is being loaded
  useEffect(() => {
    if (loading && audioRef.current) audioRef.current.pause()
  }, [loading])

  useEffect(() => {
    if (audioRef.current && audioWithLyricsRef.current) {
      audioRef.current.addEventListener('play', () =>
        audioWithLyricsRef.current.play()
      )
      audioRef.current.addEventListener('pause', () =>
        audioWithLyricsRef.current.pause()
      )
    }
  }, [audioRef.current, audioWithLyricsRef.current])

  return (
    <FacetsProvider>
      <Layout>
        <Header className="Player-Header">
          <Row align="middle" justify="space-around">
            <Col span={12}>
              {/* <FormS3Resource form={form} onFinish={startDownload} /> */}
              <FormSearchSong onFinish={handleSearchItem} />
            </Col>
            {/* <Col>
            <Button
              type="primary"
              icon={<PlaySquareOutlined />}
              onClick={form.submit}
            />
          </Col> */}
          </Row>
          <Link
            to="/"
            component={Button}
            className="switcher"
            type="primary"
            icon={<AppstoreOutlined />}
          >
            Creator
          </Link>
        </Header>
        <Content className="container">
          <Row
            className="footer"
            align="middle"
            justify="center"
            // style={{ minHeight: BIG_CANVAS_PROPS.height }}
          >
            {!!resource && !loading && <SettingsBox frameRef={frameRef} />}
            <Col ref={frameRef} className="fullscreen-enable">
              <Status
                loading={loading}
                progress={progress}
                processing={processing}
                data={resource}
                error={error}
              />
              {!!resource && !loading && (
                <CanvasSlides
                  counterProps={counterProps}
                  playerActions={playerActions}
                />
              )}
            </Col>
          </Row>
          <Row className="footer" align="middle" justify="center">
            <Col flex="auto">
              <Row>
                <Col flex="1">
                  <Text strong> Song: </Text>
                  <Text>{selectedSong.name}</Text>
                </Col>
                <Col flex="1">
                  <Text strong> Artist: </Text>
                  <Text>{selectedSong.artist_name}</Text>
                </Col>
                <Col flex="1">
                  <Text strong> Album: </Text>
                  <Text>{selectedSong.album_name}</Text>
                </Col>
              </Row>
            </Col>
            <Col flex="200px">
              <Text strong>Cheat Mode: </Text>
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                disabled={hasSongWithLyrics}
                onChange={handlePlayWithLyrics}
              />
            </Col>
          </Row>
          <Row align="middle" justify="center">
            <Col flex={1}>
              <audio
                ref={audioRef}
                controls={false}
                className="form-audio"
                src={resource && resource.audio.src}
              />
              <audio
                ref={audioWithLyricsRef}
                controls={false}
                className="form-audio"
                src={get(resource, ['audioWithLyrics', 'src'])}
                muted
              />

              <Player audio={audioRef} />
            </Col>
          </Row>
        </Content>
      </Layout>
    </FacetsProvider>
  )
}
